/**
 * Character portraits, drawn rather than sourced.
 *
 * The brief was that the portraits look consistent with each other. Sourcing or
 * generating images makes that a matter of luck — separate generations drift in
 * lighting, crop, style and palette, and the seams show worst when the faces sit
 * side by side in a grid, which is exactly where these live.
 *
 * So every portrait is the same drawing with different parameters: one canvas,
 * one light direction, one palette, one framing, one line weight. Consistency
 * is structural — it cannot drift, because there is only one illustration.
 *
 * The form is an arched niche with a halo, which is how these figures have been
 * depicted for seventeen centuries and which lines up with the arches and gold
 * already in the app. It is deliberately iconic rather than photographic: no
 * illustration can tell you what a first-century fisherman looked like, and a
 * stylised icon does not pretend to.
 *
 * To swap in real artwork later, give a character a `portrait.image` and render
 * that instead — the manifest already carries everything else.
 */

const SKIN = {
  light: { base: '#e3c3a3', shade: '#c9a483', line: '#8a6b52' },
  olive: { base: '#d3a87f', shade: '#b88a63', line: '#7a5940' },
  tan: { base: '#b8834f', shade: '#9a6a3d', line: '#5f4026' },
  deep: { base: '#8a5a36', shade: '#6f4627', line: '#452a16' },
};

const HAIR = {
  black: { base: '#241f1c', shade: '#15110f' },
  brown: { base: '#4a3324', shade: '#332217' },
  grey: { base: '#8b8279', shade: '#6d655d' },
  white: { base: '#d9d3c7', shade: '#b5aea1' },
  auburn: { base: '#6b3626', shade: '#4c2419' },
};

const ROBE = {
  sand: { base: '#c3ad86', shade: '#a8926c' },
  clay: { base: '#a85f47', shade: '#8a4a35' },
  olive: { base: '#6d7850', shade: '#55603d' },
  indigo: { base: '#3f4c7c', shade: '#2e3960' },
  wine: { base: '#7a3044', shade: '#5d2233' },
  slate: { base: '#4a5568', shade: '#37404f' },
  ochre: { base: '#bd8a3f', shade: '#9c702e' },
  ivory: { base: '#ddd5c1', shade: '#c0b7a1' },
};

/**
 * Emblems, as simple marks on the same 24-unit grid as the app's icon set so
 * they sit at the same weight. Each figure carries the symbol they are
 * traditionally shown with.
 */
const EMBLEMS = {
  keys: 'M8.5 15.5a3 3 0 1 1 2.6-1.5H17l1.5 1.5L17 17l-1-1-1 1-1.2-1.2h-2.7a3 3 0 0 1-2.6 1.2Z',
  fish: 'M4 12c2.5-3 5.5-4.5 9-4.5S19 9 20.5 12C19 15 16.5 16.5 13 16.5S6.5 15 4 12Zm12.5-.8a.8.8 0 1 0 0 1.6.8.8 0 0 0 0-1.6Z',
  boat: 'M3.5 14.5h17L18 19H6l-2.5-4.5ZM12 4v10M12 6l6 3-6 2',
  scroll: 'M6 5h11a2 2 0 0 1 0 4H6a2 2 0 0 1 0-4Zm0 0a2 2 0 0 0 0 4v10h11a2 2 0 0 0 0-4H6',
  saw: 'M4 15.5 15 5l3.5 3.5L7.5 19 4 15.5Zm2.5 1.5 9-9M4.5 19.5h5',
  scallop: 'M12 4c4.5 0 8 3.5 8 8H4c0-4.5 3.5-8 8-8Zm0 0v8M8 6.5 10 12M16 6.5 14 12',
  staff: 'M9 20V9a3.5 3.5 0 1 1 7 0v1',
  harp: 'M6 19V7c0-1.5 5-3 10-3v11M8 8v9M11 7v10M14 6v11',
  lion: 'M12 6a4 4 0 0 1 4 4v1a4 4 0 0 1-8 0v-1a4 4 0 0 1 4-4Zm-6 4 2 1m10-1-2 1M9.5 17 8 20m6.5-3 1.5 3',
  lamp: 'M5 13h11l2.5-1.5V15l-2.5-1M5 13a5 5 0 0 1 5-5h1a5 5 0 0 1 5 5M9 8V5',
  sword: 'M12 3v13M9 16h6M12 16v5M9.5 8.5h5',
  tent: 'M3 19 12 5l9 14H3Zm9-14v14',
  crown: 'M4 17h16l-1.5-8-4 3.5L12 6l-2.5 6.5-4-3.5L4 17Z',
  dove: 'M12 5a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm0 4c-1 1.8-1.5 3.8-1.5 6 0 1.6.4 3.2 1 4.6.2.4.6.4.8 0 .6-1.4 1-3 1-4.6 0-2.2-.5-4.2-1.3-6ZM10.5 11C8.5 9 6 8 3 8.5c1.2 3 3.5 5 6.8 5.6M13.5 11c2-2 4.5-3 7.5-2.5-1.2 3-3.5 5-6.8 5.6',
  'water-jar': 'M9 5h6l-1 3c2 1 3.5 3 3.5 5.5 0 3-2.5 5.5-5.5 5.5S6.5 16.5 6.5 13.5C6.5 11 8 9 10 8L9 5Z',
  'coin-purse': 'M8 8h8l2 4.5c0 3.5-2.5 6-6 6s-6-2.5-6-6L8 8Zm1.5 0V6a2.5 2.5 0 0 1 5 0v2',
  anchor: 'M12 7v13M12 4.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM8.5 10.5h7M5 14a7 7 0 0 0 14 0',
  chalice: 'M7.5 5h9l-1 5a3.5 3.5 0 0 1-7 0l-1-5ZM12 14v5M8.5 19h7',
  stones: 'M6 17a3 3 0 1 1 6 0H6Zm6 0a3.5 3.5 0 1 1 7 0h-7ZM9 11a2.5 2.5 0 1 1 5 0H9Z',
  fire: 'M13 4c-.2 2.2-1.2 3.4-2.4 4.6C9.2 10 8 11.2 8 13.4a4 4 0 0 0 8 0c0-1.6-.7-2.8-1.6-3.9-.2.8-.6 1.3-1.3 1.6.5-2.8-.1-5.2-.1-7.1Z',
  star: 'M12 4.5 14 10h5.5l-4.5 3.5 1.7 5.5L12 15.5 7.3 19 9 13.5 4.5 10H10l2-5.5Z',
  bread: 'M5 11a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4v-2Zm4-4v10m3-10v10m3-10v10',
  cross: 'M12 4v16M7 9.5h10',
  cloak: 'M12 4 7 6.5 4 20h16L17 6.5 12 4Zm0 0v16',
  needle: 'M5 19 19 5M17 5h2v2M7 15l2 2',
};

/** Whatever the data says, fall back to something rather than nothing. */
const emblemPath = (name) => EMBLEMS[name] ?? EMBLEMS.cross;

export default function CharacterPortrait({ portrait, size = 120, className = '' }) {
  const skin = SKIN[portrait?.skin] ?? SKIN.olive;
  const hair = HAIR[portrait?.hairColour] ?? HAIR.brown;
  const robe = ROBE[portrait?.robe] ?? ROBE.sand;
  const style = portrait?.hair ?? 'short';
  const beard = portrait?.beard ?? 'full';
  const headwear = portrait?.headwear ?? 'none';

  const bald = style === 'bald';
  const receding = style === 'receding';
  const covered = headwear === 'kaffiyeh' || headwear === 'turban' || headwear === 'hood' || headwear === 'veil';

  return (
    <svg
      className={`portrait${className ? ` ${className}` : ''}`}
      viewBox="0 0 120 140"
      width={size}
      height={(size * 140) / 120}
      role="img"
      aria-label="Illustrated portrait"
    >
      <defs>
        {/* One light direction for every portrait — this is most of what makes
            a set read as a set. */}
        <linearGradient id="pt-niche" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#16223a" />
          <stop offset="100%" stopColor="#0b1120" />
        </linearGradient>
        <linearGradient id="pt-halo" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#e8d59a" />
          <stop offset="100%" stopColor="#8a6f28" />
        </linearGradient>
        <clipPath id="pt-clip">
          <path d="M6 134V52a54 54 0 0 1 108 0v82Z" />
        </clipPath>
      </defs>

      {/* The niche: an arch, the same shape as the app's other arches. */}
      <path d="M6 134V52a54 54 0 0 1 108 0v82Z" fill="url(#pt-niche)" />

      <g clipPath="url(#pt-clip)">
        {/* Halo, behind the head. */}
        <circle cx="60" cy="56" r="31" fill="none" stroke="url(#pt-halo)" strokeWidth="2.5" opacity="0.85" />
        <circle cx="60" cy="56" r="31" fill="#c9a84c" opacity="0.08" />

        {/* Shoulders and robe. */}
        <path d="M60 92c-22 0-38 11-42 26v16h84v-16c-4-15-20-26-42-26Z" fill={robe.base} />
        <path d="M60 92c-22 0-38 11-42 26v16h42V92Z" fill={robe.shade} opacity="0.55" />
        {/* Neckline fold — the one bit of drapery, kept simple. */}
        <path d="M50 95l10 16 10-16" fill="none" stroke={robe.shade} strokeWidth="2.5" strokeLinecap="round" />

        {/* Neck. */}
        <path d="M52 78h16v14c0 3-3.5 5-8 5s-8-2-8-5V78Z" fill={skin.shade} />

        {/* Hair behind the head, for the long styles. */}
        {!bald && (style === 'long' || style === 'curly') ? (
          <path d="M30 58c0-18 13-30 30-30s30 12 30 30v30c-3-6-6-10-9-12 1-10-6-16-21-16s-22 6-21 16c-3 2-6 6-9 12V58Z" fill={hair.shade} />
        ) : null}

        {/* Head. */}
        <ellipse cx="60" cy="58" rx="23" ry="26" fill={skin.base} />
        {/* One shadow side, always the same side. */}
        <path d="M60 32c-12.7 0-23 11.6-23 26s10.3 26 23 26V32Z" fill={skin.shade} opacity="0.35" />

        {/* Ears. */}
        <ellipse cx="37" cy="60" rx="3.5" ry="5.5" fill={skin.shade} />
        <ellipse cx="83" cy="60" rx="3.5" ry="5.5" fill={skin.shade} />

        {/* Brows, eyes, nose, mouth — iconic rather than rendered. */}
        <path d="M48 51.5c2-1.5 6-1.5 8 0M64 51.5c2-1.5 6-1.5 8 0" fill="none" stroke={hair.shade} strokeWidth="2" strokeLinecap="round" />
        <ellipse cx="52" cy="57" rx="3" ry="2.1" fill="#fdfaf4" />
        <ellipse cx="68" cy="57" rx="3" ry="2.1" fill="#fdfaf4" />
        <circle cx="52" cy="57.2" r="1.45" fill="#2a1e14" />
        <circle cx="68" cy="57.2" r="1.45" fill="#2a1e14" />
        <path d="M60 59v7l-3 2" fill="none" stroke={skin.line} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />

        {/* Beard, sized by style. The mouth is drawn under it either way. */}
        <path d="M55 73.5c2 1.2 8 1.2 10 0" fill="none" stroke={skin.line} strokeWidth="1.8" strokeLinecap="round" />

        {/* Beards are a crescent between the jawline and a dip under the
            cheekbones, not a slab hung off the chin — the outer edge follows
            the head, the inner edge stops just above the mouth. */}
        {beard === 'stubble' ? (
          <path
            d="M42 60c0 13 6 25 18 25s18-12 18-25c-3 8-9 12-18 12s-15-4-18-12Z"
            fill={hair.base}
            opacity="0.3"
          />
        ) : null}
        {beard === 'short' ? (
          <path d="M42 59c0 15 6 27 18 27s18-12 18-27c-3 9-9 13-18 13s-15-4-18-13Z" fill={hair.base} />
        ) : null}
        {beard === 'full' ? (
          <path d="M40 56c0 22 7 36 20 36s20-14 20-36c-4 12-10 16-20 16s-16-4-20-16Z" fill={hair.base} />
        ) : null}
        {beard === 'long' ? (
          <path d="M40 56c0 30 6 50 20 50s20-20 20-50c-4 12-10 16-20 16s-16-4-20-16Z" fill={hair.base} />
        ) : null}
        {beard !== 'none' ? (
          <path d="M55 73.5c2 1.2 8 1.2 10 0" fill="none" stroke={skin.line} strokeWidth="1.6" strokeLinecap="round" opacity="0.8" />
        ) : null}

        {/* Hair on top. Skipped when a head covering would hide it anyway. */}
        {!bald && !covered ? (
          <>
            {style === 'curly' ? (
              <path d="M60 30c-14 0-24 9-25 21 3-3 6-4 8-3-1-4 2-7 6-6-1-4 3-7 7-5 3-3 8-3 11 0 4-2 8 1 7 5 4-1 7 2 6 6 2-1 5 0 8 3-1-12-11-21-28-21Z" fill={hair.base} />
            ) : receding ? (
              <path d="M37 52c1-11 10-19 23-19s22 8 23 19c-2-6-9-9-16-8-3-3-11-3-14 0-7-1-14 2-16 8Z" fill={hair.base} />
            ) : (
              <path d="M35 55c0-15 11-25 25-25s25 10 25 25c-3-8-11-13-25-13s-22 5-25 13Z" fill={hair.base} />
            )}
          </>
        ) : null}

        {/* Head coverings. */}
        {headwear === 'kaffiyeh' ? (
          <>
            <path d="M32 58c0-17 12-29 28-29s28 12 28 29c0 3-1 5-3 5-2-9-11-16-25-16s-23 7-25 16c-2 0-3-2-3-5Z" fill={robe.base} />
            <path d="M32 56c-1 14 2 26 6 34-6-4-10-14-9-27 0-3 1-6 3-7Zm56 0c1 14-2 26-6 34 6-4 10-14 9-27 0-3-1-6-3-7Z" fill={robe.shade} />
            <path d="M36 42h48" stroke={robe.shade} strokeWidth="3" strokeLinecap="round" fill="none" />
          </>
        ) : null}
        {headwear === 'turban' ? (
          <>
            <path d="M33 52c0-15 12-25 27-25s27 10 27 25c0 4-2 6-5 6-3-8-11-13-22-13s-19 5-22 13c-3 0-5-2-5-6Z" fill={robe.base} />
            <path d="M34 46c8-6 44-6 52 0M35 40c8-5 42-5 50 0" stroke={robe.shade} strokeWidth="2.5" fill="none" strokeLinecap="round" />
          </>
        ) : null}
        {headwear === 'hood' ? (
          <path d="M28 66c0-22 14-38 32-38s32 16 32 38c0 4-1 8-3 11-1-16-13-27-29-27s-28 11-29 27c-2-3-3-7-3-11Z" fill={robe.shade} />
        ) : null}
        {headwear === 'veil' ? (
          <path d="M30 70c0-23 13-40 30-40s30 17 30 40c0 8-1 15-3 21-2-20-12-33-27-33s-25 13-27 33c-2-6-3-13-3-21Z" fill={robe.base} opacity="0.92" />
        ) : null}
        {headwear === 'cap' ? (
          <path d="M40 42c0-9 8-15 20-15s20 6 20 15c-4-4-11-6-20-6s-16 2-20 6Z" fill={robe.shade} />
        ) : null}
        {headwear === 'crown' ? (
          <path d="M38 38l3-14 7 7 6-11 6 11 7-7 3 14H38Z" fill="#c9a84c" stroke="#8a6f28" strokeWidth="1.5" strokeLinejoin="round" />
        ) : null}
      </g>

      {/* The arch fillet, drawn over everything so the niche edge stays crisp. */}
      <path d="M6 134V52a54 54 0 0 1 108 0v82" fill="none" stroke="rgba(201,168,76,0.45)" strokeWidth="2" />

      {/* Emblem badge, bottom right, clear of the face. */}
      <g transform="translate(84 100)">
        <circle cx="12" cy="12" r="15" fill="#0b1120" stroke="rgba(201,168,76,0.55)" strokeWidth="1.5" />
        <path
          d={emblemPath(portrait?.emblem)}
          fill="none"
          stroke="#c9a84c"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          transform="scale(0.86) translate(2 2)"
        />
      </g>
    </svg>
  );
}

export const EMBLEM_NAMES = Object.keys(EMBLEMS);
